import { cn, useBackLayer } from '@gramas/ui'
import type { CSSProperties } from 'react'
import { useEffect, useState } from 'react'
import { useProfile } from '../screens/useProfile.js'
import { playCue } from '../sound/engine.js'
import { GhostReveal } from '../stage/GhostReveal.js'
import { Beat, Figure, Hero, Kicker, Line, Mark, Rise } from '../stage/parts.js'
import { Stage } from '../stage/Stage.js'
import {
  type MeetingBeat,
  type MeetingMotif,
  type MeetingQuestion,
  QUESTIONS,
  SLIDES,
} from './content.js'
import { markGlitimeMet } from './store.js'

export interface FirstMeetingProps {
  /** Leave. Called only once she has actually been met — see the guard below. */
  onDone: () => void
}

/**
 * The one time this app makes you sit through something.
 *
 * Every other overlay here — the tour, a sheet — may be walked away from,
 * because nothing in them is a person. This is. Skipping an introduction is
 * how you end up not knowing who you have been talking to for weeks, which
 * is exactly the situation this exists to end. No skip button, no backdrop to
 * tap through, no Escape key, no ✕, and the back gesture is unconditionally
 * intercepted for as long as this is open.
 *
 * She talks first, in sequence, and only then offers the hub — an introduction
 * is a monologue, not an interview. It is staged rather than boxed: her own
 * room, one beat per screen, swiped through (`stage/Stage.tsx`). The previous
 * cut put the same eleven beats in an eleven-times-repeated dialog card, where
 * the only thing that ever moved was the button, and the first thing anyone
 * did was hunt for a way to close it.
 */
export function FirstMeeting({ onDone }: FirstMeetingProps) {
  const [index, setIndex] = useState(0)
  const [read, setRead] = useState<ReadonlySet<string>>(new Set())
  const { profile } = useProfile()
  // Same rule as the sass cameos and the tour: the author's own account never
  // hears about the author, and material naming Eimantas only makes sense on
  // the account it is about.
  const cameoOk = profile != null && !profile.isAdmin
  const slides = SLIDES.filter((s) => !s.cameo || cameoOk)
  const questions = QUESTIONS.filter((q) => !q.cameo || cameoOk)
  const left = questions.filter((q) => !read.has(q.id)).length

  // One past the last slide is the hub; leaving the hub forwards is done.
  const onHub = index >= slides.length

  useBackLayer(true, () => {}, { guard: () => true })

  // The one sound this arc gets, on the one slide that is nothing but her
  // mark. Fired on mount rather than on `index === 0` re-renders — the
  // sequence never returns to the first slide once past it — so this plays
  // exactly once, the moment the household actually meets her.
  useEffect(() => {
    playCue('firsttime')
  }, [])

  const finish = () => {
    void markGlitimeMet()
    onDone()
  }

  return (
    <Stage
      label="Pažintis su Glitime"
      count={slides.length + 1}
      index={index}
      onIndex={setIndex}
      onFinish={finish}
      finishLabel="Pradedam"
      hue={onHub ? HUES.hub : (HUES.slides[index % HUES.slides.length] ?? HUES.hub)}
      // She is a pulse in the top corner until the slide where she says the
      // name; introducing herself in the header before she gets to it would
      // spend the one beat this arc is built around.
      crest={index === 0 ? null : 'Glitimė'}
      note="pažintis"
      // The unskippable contract, kept without a dead grey button: the way out
      // stays shut until every question has been opened, and says how many are
      // left rather than simply refusing.
      hold={onHub && left > 0 ? `Liko ${left}` : null}
    >
      {onHub ? (
        <Questions questions={questions} read={read} onReveal={(id) => setRead(add(read, id))} />
      ) : (
        <SlideBeat beat={slides[index]!} />
      )}
    </Stage>
  )
}

function add(current: ReadonlySet<string>, id: string): ReadonlySet<string> {
  return current.has(id) ? current : new Set(current).add(id)
}

/**
 * Violet through cold blue and back, with the hub in her own colour. The tint
 * is faint — it is the light in the room, not a background — but it is the
 * reason eleven slides do not read as one slide shown eleven times.
 */
const HUES = {
  slides: [
    'rgba(70, 40, 130, 0.30)',
    'rgba(40, 60, 140, 0.28)',
    'rgba(90, 30, 90, 0.26)',
    'rgba(30, 80, 120, 0.28)',
    'rgba(60, 45, 150, 0.30)',
  ],
  hub: 'rgba(80, 55, 160, 0.32)',
} as const

function SlideBeat({ beat }: { beat: MeetingBeat }) {
  return (
    <Beat top={beat.motif != null ? <Motif kind={beat.motif} /> : undefined}>
      <Hero delayMs={beat.motif == null ? 60 : 220}>{beat.title}</Hero>
      <Line delayMs={beat.motif == null ? 420 : 580}>{beat.body}</Line>
    </Beat>
  )
}

/** Four days written down, three not — the slide is about reading both. */
const SILENT_WEEK: ReadonlyArray<readonly [string, boolean]> = [
  ['pr', true],
  ['an', true],
  ['tr', false],
  ['kt', true],
  ['pn', false],
  ['št', false],
  ['sk', true],
]

/** What the household had a great deal of, and what it had none of. */
const PROOF: ReadonlyArray<readonly [string, boolean]> = [
  ['tikėjimas', true],
  ['įrodymai', false],
]

/**
 * The picture a slide brings with it.
 *
 * Deliberately drawn out of the same few primitives as the rest of the stage —
 * a dot, a bar, a word — rather than illustrated. She is a rumour in a kitchen
 * appliance; anything that looked like a mascot would answer a question the
 * whole voice is built on leaving open.
 */
function Motif({ kind }: { kind: MeetingMotif }) {
  // Nothing to look at but her: the sequence opens on this and ends on it.
  if (kind === 'mark') return <Mark />

  if (kind === 'name') {
    // The two words the name is welded from, with the seam showing.
    return (
      <Rise className="flex flex-wrap items-center gap-3 font-display text-base uppercase tracking-[0.16em]">
        <span className="text-[var(--stage-mute)]">glitimas</span>
        <span className="text-[var(--stage-glow)]">+</span>
        <span className="text-[var(--stage-mute)]">giltinė</span>
      </Rise>
    )
  }

  if (kind === 'years') {
    // Eight years of dry bread for nothing. The slide says the number in
    // words, so the digits here are a second look at it, not an echo.
    return (
      <Rise>
        <Figure value={8} tone="ink" unit="metai sausos duonos" delayMs={120} />
      </Rise>
    )
  }

  if (kind === 'proof') {
    // How much of each the household had. The empty column is the slide.
    return (
      <div className="flex items-end gap-6">
        {PROOF.map(([label, held], i) => (
          <div key={label} className="flex flex-col items-center gap-2.5">
            <span
              className={cn(
                'stage-bar block w-10 rounded-md',
                held
                  ? 'h-28 bg-[var(--stage-glow)] shadow-[0_0_22px_-4px_var(--stage-glow)]'
                  : 'h-1.5 bg-[var(--stage-line)]',
              )}
              style={{ '--i': i } as CSSProperties}
            />
            <span className="text-[10px] text-[var(--stage-mute)] uppercase tracking-[0.16em]">
              {label}
            </span>
          </div>
        ))}
      </div>
    )
  }

  if (kind === 'fee') {
    // The one number in her favour, and the household's own unit of price.
    return (
      <Rise>
        <Figure value={0} tone="ink" label="honoraras" unit="eurų per valandą" delayMs={120} />
      </Rise>
    )
  }

  if (kind === 'year') {
    // Counted up rather than printed: the slide says the year is in a file
    // somewhere and that she has stopped keeping track, and a number that
    // takes a second to arrive says that better than the digits alone. It is
    // also why this is the figure and the title is not — three printed 1863s
    // on one slide would be a typo, not a motif.
    return (
      <Rise>
        <Figure value={1863} tone="ink" delayMs={120} />
      </Rise>
    )
  }

  if (kind === 'places') {
    return (
      <div className="flex flex-wrap gap-2.5">
        {['Šaldytuvas', 'Svarstyklės', 'Orkaitė'].map((place, i) => (
          <Rise key={place} step={i}>
            <span className="rounded-full border border-[var(--stage-line)] bg-[var(--stage-glow)]/10 px-4 py-1.5 text-[var(--stage-dim)] text-sm">
              {place}
            </span>
          </Rise>
        ))}
      </div>
    )
  }

  if (kind === 'candles') {
    return (
      <div aria-hidden="true" className="flex items-end gap-4">
        {[0, 1, 2, 3, 4].map((i) => (
          <Rise key={i} step={i}>
            <span
              className="stage-flame block h-7 w-2.5 rounded-full bg-[#ffc46b]"
              style={{ animationDelay: `${i * 370}ms` }}
            />
          </Rise>
        ))}
      </div>
    )
  }

  if (kind === 'silence') {
    // Four days written down, three not — she says she reads both.
    return (
      <div aria-hidden="true" className="flex items-end gap-2.5">
        {SILENT_WEEK.map(([day, logged], i) => (
          <Rise key={day} step={i}>
            <span
              className={cn(
                'block w-3.5 rounded-full',
                logged
                  ? 'h-16 bg-[var(--stage-figure)] shadow-[0_0_16px_-2px_var(--stage-figure)]'
                  : 'h-5 bg-[var(--stage-line)]',
              )}
            />
          </Rise>
        ))}
      </div>
    )
  }

  // Katė, in the dark, where she always is.
  return (
    <Rise>
      <span aria-hidden="true" className="stage-blink flex items-center gap-4">
        <span className="block h-4 w-7 rounded-full bg-[#a6f36a] shadow-[0_0_20px_3px_rgba(166,243,106,0.6)]" />
        <span className="block h-4 w-7 rounded-full bg-[#a6f36a] shadow-[0_0_20px_3px_rgba(166,243,106,0.6)]" />
      </span>
    </Rise>
  )
}

function Questions({
  questions,
  read,
  onReveal,
}: {
  questions: readonly MeetingQuestion[]
  read: ReadonlySet<string>
  onReveal: (id: string) => void
}) {
  return (
    // One mark per question, lit as it is opened: the only slide where the way
    // out is held shut, so the thing above the text is how much of it is left.
    <Beat
      top={
        <div aria-hidden="true" className="flex items-end gap-6">
          {questions.map((q, i) => (
            <Rise key={q.id} step={i}>
              <span
                className={cn(
                  'block font-display text-[3.6rem] leading-none transition-colors duration-500',
                  read.has(q.id)
                    ? 'text-[var(--stage-glow)] drop-shadow-[0_0_28px_rgba(139,116,255,0.55)]'
                    : 'text-[var(--stage-line)]',
                )}
              >
                ?
              </span>
            </Rise>
          ))}
        </div>
      }
    >
      <Kicker>Dabar tu</Kicker>
      <Hero delayMs={80}>Turi klausimų?</Hero>
      <Line delayMs={400}>Paliesk. Aš niekur neskubu — turiu amžinybę.</Line>

      <div className="mt-1 flex flex-col gap-2">
        {questions.map((q, i) => {
          const open = read.has(q.id)
          return (
            <Rise key={q.id} step={i} delayMs={520}>
              <button
                type="button"
                onClick={() => onReveal(q.id)}
                className={cn(
                  'w-full rounded-2xl border px-4 py-3 text-left transition-colors duration-200',
                  open
                    ? 'border-[var(--stage-glow)]/45 bg-[var(--stage-glow)]/12'
                    : 'border-[var(--stage-line)] bg-white/[0.03]',
                )}
              >
                <span className="flex items-center gap-2.5">
                  <span
                    aria-hidden="true"
                    className={cn(
                      'size-1.5 shrink-0 rounded-full transition-all duration-300',
                      open
                        ? 'bg-[var(--stage-figure)] shadow-[0_0_10px_var(--stage-figure)]'
                        : 'bg-[var(--stage-mute)]',
                    )}
                  />
                  <span className="font-display font-semibold text-[15px] text-[var(--stage-ink)]">
                    {q.question}
                  </span>
                </span>
                {open && (
                  <GhostReveal className="mt-2 block pl-4 text-[var(--stage-dim)] text-sm leading-[1.5]">
                    {q.answer}
                  </GhostReveal>
                )}
              </button>
            </Rise>
          )
        })}
      </div>
    </Beat>
  )
}
